import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { IFormFileDrawingItem } from '@file/projectFile/projectFile.models';
import { Checkbox } from '@gcl/react-core';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { ETransmittalStatus, ETransmittalType } from '@transmittal/transmittal.constants';
import { IProjectTransmittalItemDocumentType } from '@transmittal/transmittalItem/transmittalItemDocumentType/transmittalItemDocumentType.models';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import './TransmittalItemTableForm.scss';
import { getMasterScheduleDetail, useNominatedInMasterSchedule } from '@masterSchedule/masterSchedule.hooks';
import { useUpsertTransmittalItemsAndRevisions } from '@transmittal/transmittalItem/transmittalItem.queries';
import { IProjectTransmittalItem } from '@transmittal/transmittalItem/transmittalItem.models';
import { IUpsertProjectTransmittalItemRevisionRequest } from '@transmittal/transmittalItemRevision/transmittalItemRevision.models';

interface ITransmittalItemTableRowTransmittalRevisionForApprovalProps {
  cellExtras: { [key: string]: any };
  projectId: number;
  masterScheduleId: number;

  tempValue: string;
  item: IFormFileDrawingItem;
  index: number;
  transmittalId: number;
  transmittalStatus?: ETransmittalStatus;
  transmittalType?: ETransmittalType;
  docType: IProjectTransmittalItemDocumentType;
  onRenderIdle: (fileItem: IFormFileDrawingItem, value: string | undefined) => ReactNode;
  setRowUploading: (index: number, isUploading: boolean) => void;
  isReadOnly?: boolean;
  queryKey: QueryKey;
}
export const TransmittalItemTableRowTransmittalRevisionForApproval = ({
  cellExtras,
  projectId,
  masterScheduleId,
  tempValue,
  item,
  index,
  transmittalId,
  transmittalStatus,
  transmittalType,
  docType,
  onRenderIdle,
  setRowUploading,
  isReadOnly,
  queryKey,
}: ITransmittalItemTableRowTransmittalRevisionForApprovalProps) => {
  const queryClient = useQueryClient();

  const attachmentId = item.transmittalItem?.transmittalRevision?.projectFile?.id;

  const { masterSchedule } = getMasterScheduleDetail(projectId, masterScheduleId, false);
  const { isNominatedChecker: isPermittedSendApproval } = useNominatedInMasterSchedule(masterSchedule);
  const [isLoadingRow, setIsLoadingRow] = useState(false);
  const { isRefetchingTransmittalItems, isWindowFocus } = cellExtras;

  const { mutationSelectAllTransmittalRevisionsForApproval } = cellExtras;
  const {
    isLoading: isLoadingSelectAllTransmittalRevisionsForApproval,
    isSuccess: isSuccessSelectAllTransmittalRevisionsForApproval,
    isError: isErrorSelectAllTransmittalRevisionsForApproval,
  } = mutationSelectAllTransmittalRevisionsForApproval;

  const mutationUpsertTransmittalItemsAndRevisions = useUpsertTransmittalItemsAndRevisions();
  const {
    mutate: mutateUpsertTransmittalItemsAndRevisions,
    isLoading: isLoadingUpsertTransmittalItemsAndRevisions,
    isError: isErrorUpsertTransmittalItemsAndRevisions,
    isSuccess: isSuccessUpsertTransmittalItemsAndRevisions,
    error: errorUpsertTransmittalItemsAndRevisions,
    reset: resetUpsertTransmittalItemsAndRevisions,
    data: dataUpsertTransmittalItemsAndRevisions,
  } = mutationUpsertTransmittalItemsAndRevisions;

  const isCertifiedFromData = useMemo(() => {
    if (transmittalType === ETransmittalType.REVIEWER)
      return dataUpsertTransmittalItemsAndRevisions?.items?.at(0)?.transmittalRevision?.isReviewerDesignCertified;
    return dataUpsertTransmittalItemsAndRevisions?.items?.at(0)?.transmittalRevision?.isIceDesignCertified;
  }, [JSON.stringify(dataUpsertTransmittalItemsAndRevisions), transmittalType]);

  const isCertifiedFromItem = useMemo(() => {
    if (transmittalType === ETransmittalType.REVIEWER) return item.form?.isReviewerDesignCertified;
    return item.form?.isIceDesignCertified;
  }, [JSON.stringify(item.form), transmittalType]);

  const [isSelectedForApproval, setIsSelectedForApproval] = useState(isCertifiedFromItem);

  useEffect(() => {
    if (item) {
      resetUpsertTransmittalItemsAndRevisions();
      setIsSelectedForApproval(
        transmittalType === ETransmittalType.REVIEWER
          ? item.form?.isReviewerDesignCertified
          : item.form?.isIceDesignCertified,
      );
    }
  }, [JSON.stringify(item)]);

  useEffect(() => {
    if (isSelectedForApproval === undefined || !item.form?.uuid || !item.form?.projectId) return;
    if (dataUpsertTransmittalItemsAndRevisions && isSelectedForApproval === isCertifiedFromData) return;
    if (!dataUpsertTransmittalItemsAndRevisions && isSelectedForApproval === isCertifiedFromItem) return;
    setIsLoadingRow(true);
    setRowUploading(index, true);
    setIsLoadingRow(true);
    const request = {
      ...(item.form as IUpsertProjectTransmittalItemRevisionRequest),
      [transmittalType === ETransmittalType.REVIEWER ? 'isReviewerDesignCertified' : 'isIceDesignCertified']:
        isSelectedForApproval,
      action: 'certify',
      projectFile: undefined,
    } as IUpsertProjectTransmittalItemRevisionRequest;

    mutateUpsertTransmittalItemsAndRevisions({
      transmittalId,
      request: [request],
    });
  }, [isSelectedForApproval]);

  useEffect(() => {
    if (!isSuccessUpsertTransmittalItemsAndRevisions) return;
    setRowUploading(index, false);
    const updatedItem = dataUpsertTransmittalItemsAndRevisions.items?.at(0);

    const prevDataList: IProjectTransmittalItem[] = queryClient.getQueryData(queryKey) ?? [];

    const newList = prevDataList.map((s) => {
      if (s.id !== updatedItem?.id) return s;
      return updatedItem;
    });
    queryClient.setQueryData(queryKey, newList);
    resetUpsertTransmittalItemsAndRevisions();
    setTimeout(() => {
      setIsLoadingRow(false);
    }, 500);
  }, [isSuccessUpsertTransmittalItemsAndRevisions]);

  if (transmittalType === ETransmittalType.DESIGNER || transmittalType === ETransmittalType.SITE) return null;

  if (transmittalStatus === ETransmittalStatus.DRAFT) {
    return (
      <div className="gcl-gems-transmittal-item-for-approval">
        <ApiErrorDisplay
          errors={[
            {
              error: errorUpsertTransmittalItemsAndRevisions,
              genericError: 'Update Project Transmittal item failed',
              reset: resetUpsertTransmittalItemsAndRevisions,
            },
          ]}
          callbackAfterToast={() => {
            setRowUploading(index, false);
            setIsLoadingRow(false);
            setIsSelectedForApproval(isCertifiedFromItem);
          }}
        />
        {onRenderIdle(item, tempValue)}
        {isPermittedSendApproval && attachmentId && item.transmittalItem?.inUse && (
          <Checkbox
            id={attachmentId.toString()}
            className={
              transmittalType === ETransmittalType.REVIEWER
                ? 'gcl-gems-transmittal-item-table-form-reviewer-checkbox'
                : ''
            }
            disabled={
              isReadOnly ||
              isRefetchingTransmittalItems ||
              isLoadingRow ||
              isLoadingSelectAllTransmittalRevisionsForApproval ||
              !isWindowFocus
            }
            checked={isSelectedForApproval}
            onChange={(checked: boolean) => {
              setIsSelectedForApproval(checked);
            }}
          />
        )}
      </div>
    );
  }
  if (transmittalStatus === ETransmittalStatus.APPROVED) {
    return (
      <div
        className={
          isCertifiedFromItem ?? false
            ? 'gcl-gems-transmittal-item-for-approval-ice-certified'
            : 'gcl-gems-transmittal-item-for-approval-not-ice-certified'
        }
      >
        {onRenderIdle(item, tempValue)}
      </div>
    );
  }
  return null;
};
